import React, { useState, useEffect } from 'react';

const LottoGenerator = () => {
  const [lottoSets, setLottoSets] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatingIndex, setGeneratingIndex] = useState(0);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  
  // 스타일 정의 (Tailwind CSS 대신 인라인 스타일 사용)
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '16px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: '24px',
      color: '#1f2937'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: '16px',
      marginBottom: '24px'
    },
    button: {
      padding: '12px 24px',
      fontWeight: 'bold',
      borderRadius: '8px',
      border: 'none',
      cursor: 'pointer',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      transition: 'background-color 0.3s'
    },
    primaryButton: {
      backgroundColor: '#8b5cf6',
      color: 'white',
    },
    secondaryButton: {
      backgroundColor: '#4b5563',
      color: 'white',
    },
    tertiaryButton: {
      backgroundColor: '#2563eb',
      color: 'white',
    },
    disabledButton: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))',
      gap: '16px'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '16px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      flexDirection: 'column'
    },
    cardHighlighted: {
      border: '2px solid #8b5cf6'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#111827'
    },
    copyButton: {
      fontSize: '14px',
      padding: '4px 12px',
      backgroundColor: '#e5e7eb',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    numbersContainer: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '16px'
    },
    ballsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '8px'
    },
    ball: {
      width: '48px',
      height: '48px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '18px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      margin: '4px'
    },
    footer: {
      marginTop: '24px',
      textAlign: 'center',
      color: '#6b7280',
      fontSize: '14px'
    }
  };

  // 로또 번호 생성 함수 (1-45 사이의 중복없는 6개 숫자)
  const generateLottoNumbers = () => {
    const numbers = [];
    while (numbers.length < 6) {
      const num = Math.floor(Math.random() * 45) + 1;
      if (!numbers.includes(num)) {
        numbers.push(num);
      }
    }
    return numbers.sort((a, b) => a - b);
  };

  // 단일 세트 재생성 효과
  const regenerateSet = (index) => {
    return new Promise(resolve => {
      // 5번 빠르게 번호 변경
      let count = 0;
      const intervalId = setInterval(() => {
        setLottoSets(prev => {
          const newSets = [...prev];
          newSets[index] = generateLottoNumbers();
          return newSets;
        });
        
        count++;
        if (count >= 5) {
          clearInterval(intervalId);
          resolve();
        }
      }, 100);
    });
  };

  // 10세트 로또 번호 순차적 생성 (애니메이션 효과)
  const generateLottoSets = async () => {
    setGenerating(true);
    setCopiedAll(false);
    setCopiedIndex(null);
    
    // 초기 10세트 생성
    const newSets = Array(10).fill().map(() => generateLottoNumbers());
    setLottoSets(newSets);
    
    // 각 세트마다 순차적으로 애니메이션 효과
    for (let i = 0; i < 10; i++) {
      setGeneratingIndex(i);
      await regenerateSet(i);
    }
    
    setGeneratingIndex(-1);
    setGenerating(false);
  };

  // 초기 로딩 시 빈 상태로 시작
  useEffect(() => {
    // 빈 번호 세트로 초기화 (0으로 채운 배열)
    resetAll();
  }, []);

  // 모든 세트 리셋
  const resetAll = () => {
    setLottoSets(Array(10).fill().map(() => Array(6).fill(0)));
    setCopiedAll(false);
    setCopiedIndex(null);
    setGeneratingIndex(-1);
    setGenerating(false);
  };

  // 개별 번호 복사 함수
  const copyNumbers = (numbers, index) => {
    // 텍스트를 직접 복사하는 방법
    const numbersText = numbers.join(', ');
    
    // 임시 텍스트 영역 생성
    const textArea = document.createElement('textarea');
    textArea.value = numbersText;
    document.body.appendChild(textArea);
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopiedIndex(index);
        setCopiedAll(false);
        setTimeout(() => setCopiedIndex(null), 2000);
      }
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
    }
    
    // 임시 요소 제거
    document.body.removeChild(textArea);
  };

  // 전체 번호 복사 함수
  const copyAllNumbers = () => {
    const allNumbersText = lottoSets.map((set, idx) => 
      `세트 #${idx + 1}: ${set.join(', ')}`
    ).join('\n');
    
    // 임시 텍스트 영역 생성
    const textArea = document.createElement('textarea');
    textArea.value = allNumbersText;
    document.body.appendChild(textArea);
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopiedAll(true);
        setCopiedIndex(null);
        setTimeout(() => setCopiedAll(false), 2000);
      }
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
    }
    
    // 임시 요소 제거
    document.body.removeChild(textArea);
  };

  // 번호 색상 결정 함수
  const getBallColor = (number) => {
    if (number <= 0) return '#d1d5db'; // 초기 상태 (회색)
    if (number <= 10) return '#fbbf24'; // 노란색
    if (number <= 20) return '#3b82f6'; // 파란색
    if (number <= 30) return '#ef4444'; // 빨간색
    if (number <= 40) return '#4b5563'; // 회색
    return '#10b981'; // 녹색
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>행운의 로또 번호 생성기</h1>
      
      <div style={styles.buttonContainer}>
        <button 
          onClick={generateLottoSets}
          disabled={generating}
          style={{
            ...styles.button,
            ...styles.primaryButton,
            ...(generating ? styles.disabledButton : {})
          }}
        >
          {generating ? '생성 중...' : '새로운 번호 생성'}
        </button>
        
        <button 
          onClick={resetAll}
          disabled={generating}
          style={{
            ...styles.button,
            ...styles.secondaryButton,
            ...(generating ? styles.disabledButton : {})
          }}
        >
          모두 지우기
        </button>
        
        <button 
          onClick={copyAllNumbers}
          disabled={generating || lottoSets.length === 0}
          style={{
            ...styles.button,
            ...styles.tertiaryButton,
            ...(generating ? styles.disabledButton : {})
          }}
        >
          {copiedAll ? '모두 복사됨!' : '전체 번호 복사'}
        </button>
      </div>
      
      <div style={styles.grid}>
        {lottoSets.map((numbers, setIndex) => (
          <div 
            key={setIndex} 
            style={{
              ...styles.card,
              ...(generatingIndex === setIndex ? styles.cardHighlighted : {})
            }}
          >
            <div style={styles.cardHeader}>
              <h3 style={styles.cardTitle}>세트 #{setIndex + 1}</h3>
              <button
                onClick={() => copyNumbers(numbers, setIndex)}
                style={styles.copyButton}
              >
                {copiedIndex === setIndex ? '복사됨!' : '번호 복사'}
              </button>
            </div>
            
            <div style={styles.numbersContainer}>
              <div style={styles.ballsContainer}>
                {numbers.map((num, numIndex) => (
                  <div 
                    key={numIndex} 
                    style={{
                      ...styles.ball,
                      backgroundColor: getBallColor(num)
                    }}
                  >
                    {num || '?'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div style={styles.footer}>
        <p>※ 이 번호는 무작위로 생성되며, 당첨을 보장하지 않습니다.</p>
        <p>※ 복권 구매는 만 19세 이상만 가능합니다. 과도한 구매는 자제해 주세요.</p>
      </div>
    </div>
  );
};

export default LottoGenerator;